import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { SubmitHandler } from "react-hook-form";
import Page from "src/layouts/Page";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import KeyShoutForm, {
	KeyShoutFormValues
} from "../../components/KeyShoutForm/KeyShoutForm";
import useKeyShout from "../../hooks/useKeyShout";
import useEditKeyShout from "../../hooks/useEditKeyShout";
import useDeleteKeyShout from "../../hooks/useDeleteKeyShout";
import { adaptEditKeyShoutPayload } from "../../adapters/adaptKeyShoutPayload";
import { formValuesToKeyShout } from "../../adapters/formValuesToKeyShout";

function keyShoutToFormValues(shout: KeyShout): KeyShoutFormValues {
	return {
		heading: shout.heading,
		content: shout.content ?? "",
		buttonLink: shout.buttonLink ?? "",
		webview: shout.webview,
		image: null
	};
}

const EditKeyShout = () => {
	const navigate = useNavigate();
	const { keyShoutId } = useParams<{ keyShoutId: string }>();
	const id = Number(keyShoutId);
	const isValidId = Number.isInteger(id) && id > 0;

	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [defaultValues, setDefaultValues] = useState<KeyShoutFormValues>({
		heading: "",
		content: "",
		buttonLink: "",
		webview: true,
		image: null
	});

	const { data: keyShout } = useKeyShout(isValidId ? id : 0);
	const { mutate: editKeyShout, isLoading: isEditing } = useEditKeyShout();
	const { mutate: deleteKeyShout, isLoading: isDeleting } = useDeleteKeyShout();

	useEffect(() => {
		if (keyShout) {
			setDefaultValues(keyShoutToFormValues(keyShout));
		}
	}, [keyShout]);

	if (!isValidId) {
		return <Navigate to="/app/homepage/key-shouts" replace />;
	}

	const handleSubmit: SubmitHandler<KeyShoutFormValues> = (data) => {
		const payload = adaptEditKeyShoutPayload(
			formValuesToKeyShout(data, id),
			data.image
		);

		editKeyShout(
			{ id, payload },
			{
				onSuccess: () => navigate("/app/homepage/key-shouts")
			}
		);
	};

	const handleDelete = () => {
		deleteKeyShout(id, {
			onSuccess: () => navigate("/app/homepage/key-shouts")
		});
	};

	return (
		<Page>
			<KeyShoutForm
				defaultValues={defaultValues}
				onSubmit={handleSubmit}
				isSubmitting={isEditing}
				isEdit
				onDelete={() => setShowDeleteModal(true)}
				existingImageUrl={keyShout?.image ?? null}
			/>

			<ConfirmationModal
				isOpen={showDeleteModal}
				title="Delete key shout?"
				description="This action cannot be undone."
				onCancel={() => setShowDeleteModal(false)}
				onSubmit={handleDelete}
				submitBtnProps={{ isLoading: isDeleting }}
			/>
		</Page>
	);
};

export default EditKeyShout;
